import React, { useState } from 'react';
import { NavLink } from 'react-router-dom';
import {
  LayoutDashboard,
  FolderOpen,
  Newspaper,
  Vote,
  FileText,
  User,
  Menu,
  X,
} from 'lucide-react';
import { investorNavItems } from '../../modules/investorRelations/nav';
import type { NavItem } from '../../modules/types';

// Map string icon names (from nav.ts) to Lucide components
const ICON_MAP: Record<string, React.ComponentType<{ className?: string }>> = {
  LayoutDashboard,
  FolderOpen,
  Newspaper,
  Vote,
  FileText,
  User,
};

function NavIcon({ name, className }: { name?: string; className?: string }) {
  if (!name) return null;
  const Icon = ICON_MAP[name];
  if (!Icon) return null;
  return <Icon className={className} />;
}

export function InvestorPortalNav() {
  const [mobileOpen, setMobileOpen] = useState(false);

  const renderLink = (item: NavItem) => (
    <NavLink
      key={item.path}
      to={item.path}
      end={item.path === '/investors'}
      onClick={() => setMobileOpen(false)}
      className={({ isActive }) =>
        `flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors min-h-[44px] ${
          isActive
            ? 'bg-blue-50 text-blue-700'
            : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
        }`
      }
    >
      <NavIcon name={item.icon} className="w-4 h-4 flex-shrink-0" />
      <span>{item.label}</span>
    </NavLink>
  );

  // The top bar groups these destinations into dropdowns; a sidebar has the
  // room to show them all, so a group becomes a heading over its children.
  // Rendering only the group itself would hide every page beneath it.
  const navContent = (
    <div className="space-y-1">
      {investorNavItems.map((item) =>
        item.children && item.children.length > 0 ? (
          <div key={item.path} className="pt-3 first:pt-0">
            <p className="px-3 pb-1 text-[11px] font-black uppercase tracking-[0.1em] text-slate-400">
              {item.label}
            </p>
            <div className="space-y-1">{item.children.map(renderLink)}</div>
          </div>
        ) : (
          renderLink(item)
        )
      )}
    </div>
  );

  return (
    <>
      {/* Desktop sidebar */}
      <nav className="hidden md:block w-64 bg-white border-r border-slate-200 min-h-screen p-4 flex-shrink-0">
        {navContent}
      </nav>

      {/* Mobile: hamburger toggle + slide-in drawer */}
      <div className="md:hidden">
        <button
          onClick={() => setMobileOpen((prev) => !prev)}
          className="fixed top-[68px] left-4 z-40 p-2 rounded-lg bg-white border border-slate-200 shadow-sm"
          aria-label="Toggle navigation"
        >
          {mobileOpen ? (
            <X className="w-5 h-5 text-slate-600" />
          ) : (
            <Menu className="w-5 h-5 text-slate-600" />
          )}
        </button>

        {mobileOpen && (
          <>
            {/* Backdrop */}
            <div
              className="fixed inset-0 bg-black/20 z-30"
              onClick={() => setMobileOpen(false)}
            />
            {/* Drawer */}
            <nav className="fixed top-[65px] left-0 bottom-0 w-64 bg-white border-r border-slate-200 p-4 z-40 overflow-y-auto">
              {navContent}
            </nav>
          </>
        )}
      </div>
    </>
  );
}
